import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

const Pagination = ({
    data = [],
    currentPage = 1,
    itemsPerPage = 10,
    onPageChange,
    onItemsPerPageChange,
    itemsPerPageOptions = [10, 20, 50, 100],
    showItemsPerPageSelector = true,
    showPageInfo = true,
    position = "bottom-center", // "bottom-right", "bottom-center", "bottom-left", "center"
    className = "",
    defaultStyle="flex items-center justify-between gap-4 sm:grid sm:grid-cols-3 sm:gap-4 box bg-theme-color  rounded-full  p-3 py-2 sm:p-4 sm:py-3 m-auto fixed bottom-8 -translate-x-1/2 w-fit"
}) => {
 const { t, } = useTranslation();
    // console.log("Pagination received data", data);


    // Calculate pagination data
    const paginationData = useMemo(() => {
        const totalItems = data?.pagination?.totalRecords ?? (Array.isArray(data?.allData) ? data.allData.length : 0);
        const totalPages = Math.max(1, Math.ceil((totalItems || 0) / itemsPerPage));
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        // Do NOT slice here; assume server already returned only the current page's items
        const currentItems = Array.isArray(data?.allData) ? data.allData : [];

        return {
            currentItems,
            totalPages,
            totalItems,
            startItem: totalItems === 0 ? 0 : indexOfFirstItem + 1,
            endItem: Math.min(indexOfLastItem, totalItems)
        };
    }, [data, currentPage, itemsPerPage]);

    // Determine component visibility
    const shouldShowItemsPerPageSelector = useMemo(() => {
        return showItemsPerPageSelector && data?.pagination?.totalRecords > 0;
    }, [showItemsPerPageSelector, data?.pagination?.totalRecords]);

    const shouldShowPaginationControls = useMemo(() => {
        return data?.pagination?.totalRecords > 0;
    }, [data?.pagination?.totalRecords]);

    const shouldRenderComponent = useMemo(() => {
        return data?.pagination?.totalRecords > 0;
    }, [data?.pagination?.totalRecords]);

const pageNumbers = useMemo(() => {
  const total = paginationData.totalPages;
  const current = currentPage;

  if (total <= 3) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 2) return [1, 2, 3];
  if (current >= total - 1) return [total - 2, total - 1, total];

  return [current - 1, current, current + 1];
}, [currentPage, paginationData.totalPages]);


    // Handlers
    const handlePageClick = useCallback((page) => {
        if (typeof page === 'number' && page !== currentPage && page >= 1 && page <= paginationData.totalPages) {
            onPageChange?.(page);
        }
    }, [currentPage, paginationData, onPageChange]); // Corrected for consistency

    const handlePreviousPage = useCallback(() => {
        if (currentPage > 1) {
            onPageChange?.(currentPage - 1);
        }
    }, [currentPage, onPageChange]);

    const handleNextPage = useCallback(() => {
        if (currentPage < paginationData.totalPages) {
            onPageChange?.(currentPage + 1);
        }
    }, [currentPage, paginationData, onPageChange]); // Corrected

    const handleFirstPage = useCallback(() => {
        if (currentPage > 1) {
            onPageChange?.(1);
        }
    }, [currentPage, onPageChange]);

    const handleLastPage = useCallback(() => {
        if (currentPage < paginationData.totalPages) {
            onPageChange?.(paginationData.totalPages);
        }
    }, [currentPage, paginationData, onPageChange]); // Corrected

    const handleItemsPerPageChange = useCallback((e) => {
        const newItemsPerPage = parseInt(e.target.value, 10);
        if (onItemsPerPageChange && !isNaN(newItemsPerPage)) {
            onItemsPerPageChange(newItemsPerPage);
        }
    }, [onItemsPerPageChange]);

    // Responsive position classes - fixed at bottom but properly centered
    const getPositionClasses = () => {
        switch (position) {
            case "bottom-center":
            case "center":
                return "fixed bottom-0 left-0 right-0 w-full flex justify-center";
            case "bottom-left":
                return "fixed bottom-0 left-0 right-0 w-full flex justify-start";
            case "bottom-right":
                return "fixed bottom-0 left-0 right-0 w-full flex justify-end";
            default:
                return "fixed bottom-0 left-0 right-0 w-full flex justify-center";
        }
    };

    if (!shouldRenderComponent) {
        return null;
    }

    const isOnlyOnePage = paginationData.totalPages <= 1;

    return (
        <div className={`py-2 px-2 sm:py-4 sm:px-4 ${className} w-fit m-auto`}>
            {/* Main container with responsive layout */}
            <div className={defaultStyle}>

                {/* Left Side: Items per page selector - Hidden on mobile */}
                <div className="hidden sm:flex justify-center md:justify-start">
                    {shouldShowItemsPerPageSelector && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <select
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                className="px-2 py-1 border border-gray-300 rounded-md styled-text focus:outline-none focus:ring-2 focus:ring-theme-headerBg focus:border-theme-headerBg text-sm min-w-[50px] transition-all"
                            >
                                {itemsPerPageOptions.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            <span className="styled-text-secondry">{t('perPage')}</span>
                        </div>
                    )}
                </div>

                {/* Mobile: Compact items per page selector */}
                <div className="flex sm:hidden items-center">
                    {shouldShowItemsPerPageSelector && (
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="px-1 py-1 border border-gray-300 rounded styled-text-secondry focus:outline-none focus:ring-1 focus:ring-theme-headerBg focus:border-theme-headerBg text-xs min-w-[40px] transition-all"
                        >
                            {itemsPerPageOptions.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Center: Pagination Controls - Compact on mobile */}
                <div className="flex justify-center flex-1 sm:flex-initial">
                    {shouldShowPaginationControls && (
                        <div className="flex items-center gap-0.5 sm:gap-1">
                            {/* First Page Button - Hidden on mobile */}
                            <button
                                onClick={handleFirstPage}
                                disabled={currentPage === 1 || isOnlyOnePage}
                                className="flex w-6 h-6 sm:w-8 sm:h-8 items-center justify-center rounded-full styled-text-secondry hover:bg-theme-dropdownHeqadibg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors duration-200"
                                aria-label="First page"
                                title="First page"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>

                            {/* Previous Button */}
                            <button
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1 || isOnlyOnePage}
                                className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full styled-text-secondry hover:bg-theme-dropdownHeqadibg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors duration-200"
                                aria-label="Previous page"
                                title="Previous page"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>

                            {/* Page Numbers - Fewer on mobile */}
                          {/* Page Numbers - ONLY 3 (prev | current | next) */}
                            <div className="flex items-center gap-1 mx-1">
                            {pageNumbers.map((page) => {
                                const isActive = page === currentPage;
                                return (
                                <button
                                    key={page}
                                    onClick={() => handlePageClick(page)}
                                    disabled={isOnlyOnePage}
                                    className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm rounded-full transition-all duration-200 ${
                                    isActive
                                        ? "bg-theme-headerBg text-white font-semibold shadow"
                                        : "styled-text font-medium hover:bg-theme-dropdownHeqadibg"
                                    } ${isOnlyOnePage ? "cursor-not-allowed opacity-50" : ""}`}
                                >
                                    {page}
                                </button>
                                );
                            })}
                            </div>


                            {/* Mobile Page Info - Compact */}
                            <div className="flex sm:hidden items-center text-xs text-gray-600 font-medium px-2">
                                {currentPage}/{paginationData.totalPages}
                            </div>

                            {/* Next Button */}
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === paginationData.totalPages || isOnlyOnePage}
                                className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full styled-text-secondry hover:bg-theme-dropdownHeqadibg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors duration-200"
                                aria-label="Next page"
                                title="Next page"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            {/* Last Page Button - Hidden on mobile */}
                            <button
                                onClick={handleLastPage}
                                disabled={currentPage === paginationData.totalPages || isOnlyOnePage}
                                className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full styled-text-secondry hover:bg-theme-dropdownHeqadibg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors duration-200"
                                aria-label="Last page"
                                title="Last page"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>

                {/* Right Side: Page Info - Compact on mobile */}
                <div className="hidden sm:flex justify-center md:justify-end">
                    {showPageInfo && (
                        <div className="flex items-center gap-1.5 text-sm styled-text-secondry">
                            <span className="font-semibold styled-text-secondry">
                                {paginationData.startItem}-{paginationData.endItem}
                            </span>
                            <span>of</span>
                            <span className="font-semibold styled-text-secondry">{paginationData.totalItems}</span>
                        </div>
                    )}
                </div>

                {/* Mobile: Compact info */}
                <div className="flex sm:hidden items-center">
                    {showPageInfo && (
                        <div className="text-xs styled-text-secondry">
                            {paginationData.totalItems}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

// Export both the component and a hook for easy state management
// export const usePagination = (initialItemsPerPage = 20) => {
//     const [currentPage, setCurrentPage] = React.useState(1);
//     const [itemsPerPage, setItemsPerPage] = React.useState(initialItemsPerPage);

//     //   console.log("current page ---->", currentPage, itemsPerPage);  


//     const handlePageChange = React.useCallback((page) => {
//         setCurrentPage(page);
//     }, []);

//     const handleItemsPerPageChange = React.useCallback((newItemsPerPage) => {
//         setItemsPerPage(newItemsPerPage);
//         setCurrentPage(1); // Reset to first page when changing items per page
//     }, []);

//     const resetPagination = React.useCallback((resetItemsPerPage = false) => {
//         setCurrentPage(1);
//         if (resetItemsPerPage) {
//             setItemsPerPage(initialItemsPerPage);
//         }
//     }, [initialItemsPerPage]);

//     const getPaginatedData = React.useCallback((data = []) => {
//         const totalItems = data?.pagination?.totalRecords ?? (Array.isArray(data?.allData) ? data.allData.length : 0);
//         const totalPages = Math.max(1, Math.ceil((totalItems || 0) / itemsPerPage));
//         const indexOfLastItem = currentPage * itemsPerPage;
//         const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//         // Do NOT slice here; server already returns current page slice
//         const currentItems = Array.isArray(data?.allData) ? data.allData : [];

//         return {
//             currentItems,
//             totalPages,
//             totalItems,
//             startItem: totalItems === 0 ? 0 : indexOfFirstItem + 1,
//             endItem: Math.min(indexOfLastItem, totalItems)
//         };
//     }, [currentPage, itemsPerPage]);

//     return {
//         currentPage,
//         itemsPerPage,
//         handlePageChange,
//         handleItemsPerPageChange,
//         resetPagination,
//         getPaginatedData
//     };
// };

// Updated usePagination hook with support for multiple tables
export const usePagination = (initialItemsPerPage = 20) => {
    // Receipt pagination
    const [currentPage, setCurrentPage] = React.useState(1);
    const [itemsPerPage, setItemsPerPage] = React.useState(initialItemsPerPage);

    // Payment pagination
    const [paymentCurrentPage, setPaymentCurrentPage] = React.useState(1);
    const [paymentItemsPerPage, setPaymentItemsPerPage] = React.useState(initialItemsPerPage);

    // Sales pagination
    const [salesCurrentPage, setSalesCurrentPage] = React.useState(1);
    const [salesItemsPerPage, setSalesItemsPerPage] = React.useState(initialItemsPerPage);

    // Purchase pagination
    const [purchaseCurrentPage, setPurchaseCurrentPage] = React.useState(1);
    const [purchaseItemsPerPage, setPurchaseItemsPerPage] = React.useState(initialItemsPerPage);

    // Journal pagination
    const [journalCurrentPage, setJournalCurrentPage] = React.useState(1);
    const [journalItemsPerPage, setJournalItemsPerPage] = React.useState(initialItemsPerPage);

    const handlePageChange = React.useCallback((page, table = 'receipt') => {
        switch (table) {
            case 'receipt':
                setCurrentPage(page);
                break;
            case 'payment':
                setPaymentCurrentPage(page);
                break;
            case 'sales':
                setSalesCurrentPage(page);
                break;
            case 'purchase':
                setPurchaseCurrentPage(page);
                break;
            case 'journal':
                setJournalCurrentPage(page);
                break;
            default:
                setCurrentPage(page);
        }
    }, []);

    const handleItemsPerPageChange = React.useCallback((newItemsPerPage, table = 'receipt') => {
        switch (table) {
            case 'receipt':
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
                break;
            case 'payment':
                setPaymentItemsPerPage(newItemsPerPage);
                setPaymentCurrentPage(1);
                break;
            case 'sales':
                setSalesItemsPerPage(newItemsPerPage);
                setSalesCurrentPage(1);
                break;
            case 'purchase':
                setPurchaseItemsPerPage(newItemsPerPage);
                setPurchaseCurrentPage(1);
                break;
            case 'journal':
                setJournalItemsPerPage(newItemsPerPage);
                setJournalCurrentPage(1);
                break;
            default:
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
        }
    }, []);

    const resetPagination = React.useCallback((resetItemsPerPage = false, table = 'all') => {
        if (table === 'all' || table === 'receipt') {
            setCurrentPage(1);
            if (resetItemsPerPage) setItemsPerPage(initialItemsPerPage);
        }
        if (table === 'all' || table === 'payment') {
            setPaymentCurrentPage(1);
            if (resetItemsPerPage) setPaymentItemsPerPage(initialItemsPerPage);
        }
        if (table === 'all' || table === 'sales') {
            setSalesCurrentPage(1);
            if (resetItemsPerPage) setSalesItemsPerPage(initialItemsPerPage);
        }
        if (table === 'all' || table === 'purchase') {
            setPurchaseCurrentPage(1);
            if (resetItemsPerPage) setPurchaseItemsPerPage(initialItemsPerPage);
        }
        if (table === 'all' || table === 'journal') {
            setJournalCurrentPage(1);
            if (resetItemsPerPage) setJournalItemsPerPage(initialItemsPerPage);
        }
    }, [initialItemsPerPage]);

    const getPaginatedData = React.useCallback((data = [], table = 'receipt') => {
        let current, items;
        
        switch (table) {
            case 'payment':
                current = paymentCurrentPage;
                items = paymentItemsPerPage;
                break;
            case 'sales':
                current = salesCurrentPage;
                items = salesItemsPerPage;
                break;
            case 'purchase':
                current = purchaseCurrentPage;
                items = purchaseItemsPerPage;
                break;
            case 'journal':
                current = journalCurrentPage;
                items = journalItemsPerPage;
                break;
            default:
                current = currentPage;
                items = itemsPerPage;
        }

        const totalItems = data?.pagination?.totalRecords ?? (Array.isArray(data?.allData) ? data.allData.length : 0);
        const totalPages = Math.max(1, Math.ceil((totalItems || 0) / items));
        const indexOfLastItem = current * items;
        const indexOfFirstItem = indexOfLastItem - items;
        const currentItems = Array.isArray(data?.allData) ? data.allData : [];

        return {
            currentItems,
            totalPages,
            totalItems,
            startItem: totalItems === 0 ? 0 : indexOfFirstItem + 1,
            endItem: Math.min(indexOfLastItem, totalItems)
        };
    }, [currentPage, itemsPerPage, paymentCurrentPage, paymentItemsPerPage, 
        salesCurrentPage, salesItemsPerPage, purchaseCurrentPage, purchaseItemsPerPage,
        journalCurrentPage, journalItemsPerPage]);

    return {
        // Receipt
        currentPage,
        itemsPerPage,
        
        // Payment
        paymentCurrentPage,
        paymentItemsPerPage,
        
        // Sales
        salesCurrentPage,
        salesItemsPerPage,
        
        // Purchase
        purchaseCurrentPage,
        purchaseItemsPerPage,
        
        // Journal
        journalCurrentPage,
        journalItemsPerPage,
        
        // Common handlers
        handlePageChange,
        handleItemsPerPageChange,
        resetPagination,
        getPaginatedData
    };
};

export default React.memo(Pagination);